import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { refreshTokenApi } from '../utils/authApi';

// Create the authentication context
export const AuthContext = createContext();

/**
 * Authentication Provider component that manages authentication state
 * and provides authentication methods to the application
 */
export function AuthProvider({ children }) {
  // Authentication state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [hasPaid, setHasPaid] = useState(false);
  const isMountedRef = useRef(true); // Track if component is mounted
  
  /**
   * Helper: fetch payment status for current user
   * @private Internal helper function
   */
  const fetchPaymentStatus = useCallback(async () => {
    try {
      console.log('[AuthContext] Fetching payment status from /api/payment/status');
      
      const resp = await fetch('/api/payment/status', {
        credentials: 'include',
      });
      
      console.log('[AuthContext] Payment status response:', {
        status: resp.status,
        statusText: resp.statusText,
        ok: resp.ok
      });
      
      if (!resp.ok) {
        if (resp.status === 401) {
          console.warn('[AuthContext] Authentication failed when fetching payment status - user may need to re-login');
          // Don't clear user state here as it might cause React error 426
          return;
        }
        console.warn('[AuthContext] Failed to fetch payment status, status:', resp.status);
        return;
      }
      
      const data = await resp.json();
      console.log('[AuthContext] Payment status data:', data);
      
      const newHasPaid = (data.totalPaid || 0) > 0;
      console.log('[AuthContext] Setting hasPaid to:', newHasPaid);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setHasPaid(newHasPaid);
      } else {
        console.log('[AuthContext] Component unmounted, skipping hasPaid state update');
      }
      
    } catch (e) {
      console.error('[AuthContext] Exception while fetching payment status:', {
        error: e.message,
        stack: e.stack,
        name: e.name
      });
    }
  }, []); // No dependencies needed
  
  // Check for existing session on component mount
  useEffect(() => {
    // Fallback timeout to ensure loading is cleared even if something goes wrong
    const loadingTimeout = setTimeout(() => {
      console.warn('[AuthContext] Auth check timeout - forcing loading to false');
      setLoading(false); // Always clear loading, even if unmounted
    }, 10000); // 10 second timeout

    const checkAuthStatus = async () => {
      const checkId = Math.random().toString(36).substr(2, 9);
      try {
        console.log(`[AuthContext] [${checkId}] Checking authentication status... (mounted:`, isMountedRef.current, ')');
        setLoading(true);
        
        // Skip config check - ConfigContext handles this, just check auth directly
        
        // Try to get user info from the protected endpoint
        console.log(`[AuthContext] [${checkId}] Fetching user info from protected endpoint`);
        const response = await fetch('/api/user', {
          credentials: 'include', // Important: include cookies in request
        });
        
        console.log(`[AuthContext] [${checkId}] User endpoint response status:`, response.status);
        if (response.ok) {
          const data = await response.json();
          console.log(`[AuthContext] [${checkId}] User authenticated successfully`);
          // Only update state if component is still mounted
          if (isMountedRef.current) {
            setUser(data.user);
            setError(null);
          }
          // Fetch payment status for authenticated user
          await fetchPaymentStatus();
          
          // Get the CSRF token from cookies
          const csrfToken = getCookie('csrf_token');
          console.log('[AuthContext] CSRF token in cookies:', csrfToken ? 'present' : 'missing');
          // Only update state if component is still mounted
          if (isMountedRef.current) {
            setCsrfToken(csrfToken);
          }
        } else if (response.status === 401) {
          // 401 is expected for unauthenticated users
          console.log(`[AuthContext] [${checkId}] User not authenticated - clearing auth state`);
          // Only update state if component is still mounted
          if (isMountedRef.current) {
            setUser(null);
            setError(null);
            setCsrfToken('');
            setHasPaid(false); // Clear payment status for unauthenticated users
          }
        } else {
          // Other error responses
          console.warn(`[AuthContext] [${checkId}] Unexpected response from auth check:`, response.status);
          // Only update state if component is still mounted
          if (isMountedRef.current) {
            setUser(null);
            setError(null); // Don't show error on initial page load
            setCsrfToken('');
            setHasPaid(false); // Clear payment status for error cases
          }
        }
      } catch (error) {
        console.error(`[AuthContext] [${checkId}] Error during auth check:`, error);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setUser(null);
          setError('Authentication check failed');
          setCsrfToken('');
          setHasPaid(false); // Clear payment status for error cases
        }
      } finally {
        // Always clear loading state, even if component unmounted (prevents infinite spinner in StrictMode)
        console.log(`[AuthContext] [${checkId}] Auth check complete, setting loading to false (mounted:`, isMountedRef.current, ')');
        setLoading(false);
        // Clear the timeout since auth check completed
        clearTimeout(loadingTimeout);
      }
    };
    
    checkAuthStatus();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMountedRef.current = false;
      clearTimeout(loadingTimeout);
    };
  }, [fetchPaymentStatus]);
  
  /**
   * Handle Google sign-in token verification
   * @param {string} idToken - Google ID token
   * @returns {Promise<Object>} - Authentication result
   */
  const handleGoogleSignIn = async (idToken) => {
    console.log('[AUTH] Starting Google sign-in process...');
    setLoading(true);
    setError(null);
    try {
      if (!idToken) {
        console.error('[AUTH] Missing authentication token');
        throw new Error('Missing authentication token');
      }

      console.log('[AUTH] Sending credential to /auth/verify');
      const response = await fetch('/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: idToken }),
        credentials: 'include',
      });

      console.log('[AUTH] /auth/verify response status:', response.status);
      const data = await response.json().catch(() => null);
      console.log('[AUTH] Response data:', data ? 'received' : 'null');

      if (!response.ok || !data || !data.success) {
        const errorMsg = data?.error || 'Authentication failed';
        console.error('[AUTH] Login failed:', errorMsg, 'Status:', response.status);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError(errorMsg);
        }
        return { success: false, error: errorMsg };
      }

      // Success Path
      console.log('[AUTH] Login successful, received user data:', {
        email: data.user?.email,
        name: data.user?.name,
        hasCsrfToken: !!data.csrf_token
      });
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setUser(data.user);
        setCsrfToken(data.csrf_token);
      }
      
      console.log('[AUTH] Fetching payment status...');
      fetchPaymentStatus();
      
      console.log('[AUTH] Setting CSRF token cookie');
      document.cookie = `csrf_token=${data.csrf_token}; path=/; max-age=3600; samesite=strict`;
      
      console.log('[AUTH] Setting login success flag in sessionStorage');
      sessionStorage.setItem('loginSuccess', 'true');
      
      const jwt = getCookie('auth_token');
      console.log('[AUTH] Auth token cookie:', jwt ? 'present' : 'missing');
      if (jwt) {
        console.log('[AUTH] Scheduling token refresh');
        scheduleRefresh(jwt);
      }
      
      console.log('[AUTH] Login process completed successfully');
      return { success: true, user: data.user };

    } catch (err) {
      const errorMessage = err.message === 'Missing authentication token' 
        ? err.message 
        : 'Sign in failed';
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError(errorMessage);
      }
      return { success: false, error: errorMessage };
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };
  
  /**
   * Refresh the authentication token by calling the centralized API utility.
   * This function updates the application state based on the refresh result.
   * @returns {Promise<boolean>} - Whether the refresh was successful
   */
  const refreshToken = async () => {
    console.log('[AuthContext] Attempting to refresh authentication token via refreshTokenApi');
    const success = await refreshTokenApi();
    if (success) {
      // The API function already updated the cookie, now update the state
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setCsrfToken(getCookie('csrf_token'));
      }
      console.log('[AuthContext] Token refresh successful, state updated.');
      
      // After manual refresh, schedule the next one
      const jwt = getCookie('auth_token');
      if (jwt) scheduleRefresh(jwt);
    } else {
      // If refresh fails, the user needs to log in again
      console.warn('[AuthContext] Token refresh failed, clearing user session.');
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setUser(null);
        setCsrfToken('');
      }
    }
    return success;
  };
  
  const scheduleRefresh = (jwt) => {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      if (!payload.exp) return;
      const expiryMs = payload.exp * 1000;
      const now = Date.now();
      const refreshAt = expiryMs - 60 * 1000; // 1 minute before expiry
      const delay = refreshAt - now;
      if (delay > 0) {
        console.log('[AuthContext] Scheduling token refresh in', delay / 1000, 'seconds');
        setTimeout(async () => {
          const ok = await refreshToken();
          if (ok) {
            // recursive scheduling with new token
            const newJwt = getCookie('auth_token');
            if (newJwt) scheduleRefresh(newJwt);
          }
        }, delay);
      }
    } catch (e) {
      console.warn('[AuthContext] Failed to schedule refresh:', e.message);
    }
  };
  
  /**
   * Refresh the user's payment status
   * Public method to refresh payment status from backend
   * @returns {Promise<void>}
   */
  const refreshUserStatus = useCallback(async () => {
    if (!user) {
      console.warn('[AuthContext] Cannot refresh user status - no user logged in');
      return;
    }
    
    console.log('[AuthContext] Manually refreshing payment status');
    
    try {
      await fetchPaymentStatus();
      console.log('[AuthContext] Payment status refresh completed successfully');
    } catch (error) {
      console.error('[AuthContext] Error refreshing payment status:', {
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      // Don't throw the error to prevent React error 426
    }
  }, [user, fetchPaymentStatus]); // Only recreate when user or fetchPaymentStatus changes

  /**
   * Log the user out
   * @returns {Promise<boolean>} - Whether logout was successful
   */
  const logout = async () => {
    try {
      console.log('[AUTH] Attempting to log user out');
      setLoading(true);
      const currentCsrfToken = csrfToken || getCookie('csrf_token');
      console.log('[AUTH] Using CSRF token for logout:', currentCsrfToken ? 'present' : 'missing');
      
      const response = await fetch('/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken
        },
        credentials: 'include'
      });
      
      console.log('[AUTH] Logout response status:', response.status);
      
      if (response.ok) {
        console.log('[AUTH] Logout successful');
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setUser(null);
          setHasPaid(false);
          setCsrfToken(null);
          setError(null);
        }
        return true;
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Logout failed' }));
        console.error('[AUTH] Logout failed:', errorData);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError(errorData.error || 'Logout failed');
        }
        return false;
      }
    } catch (err) {
      console.error('[AUTH] Logout error:', err);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError('Network error during logout');
      }
      return false;
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };
  
  /**
   * Helper function to get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null if not found
   */
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    const cookieValue = parts.length === 2 ? parts.pop().split(';').shift() : null;
    console.log(`[AuthContext] Getting cookie '${name}':`, cookieValue ? 'present' : 'not found');
    return cookieValue;
  };

  // Provide auth context value to consumers
  const contextValue = {
    user,
    loading,
    error,
    csrfToken,
    hasPaid,
    isAuthenticated: !!user,
    handleGoogleSignIn,
    refreshToken,
    refreshUserStatus,
    logout
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use the authentication context
 * @returns {Object} - Authentication context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


